/**
 * Date Formatting Utilities
 * 
 * All dates are stored in UTC on the backend. This utility formats dates
 * according to the user's saved timezone preference.
 * 
 * Usage:
 *   import { formatDate, formatDateTime, formatRelativeTime } from '../utils/dateUtils';
 *   
 *   // In a component with access to user context:
 *   const userTimezone = user?.timezone || 'UTC';
 *   formatDate(someDate, userTimezone);  // "Jan 15, 2026"
 *   formatDateTime(someDate, userTimezone);  // "Jan 15, 2026, 3:45 PM"
 */

/**
 * Formats a date string or Date object according to the user's timezone.
 * Returns a short date format like "Jan 15, 2026"
 * 
 * @param {string|Date|null} dateInput - ISO date string or Date object
 * @param {string} timezone - IANA timezone name (e.g., "America/New_York")
 * @param {object} options - Additional Intl.DateTimeFormat options
 * @returns {string} Formatted date string, or empty string if invalid
 */
export const formatDate = (dateInput, timezone = 'UTC', options = {}) => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: timezone,
      ...options
    };
    
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
  } catch (error) {
    console.warn('Date formatting error:', error);
    return '';
  }
};

/**
 * Formats a date string or Date object with time according to the user's timezone.
 * Returns a format like "Jan 15, 2026, 3:45 PM"
 * 
 * @param {string|Date|null} dateInput - ISO date string or Date object
 * @param {string} timezone - IANA timezone name (e.g., "America/New_York")
 * @param {object} options - Additional Intl.DateTimeFormat options
 * @returns {string} Formatted date/time string, or empty string if invalid
 */
export const formatDateTime = (dateInput, timezone = 'UTC', options = {}) => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
      ...options
    };
    
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
  } catch (error) {
    console.warn('DateTime formatting error:', error);
    return '';
  }
};

/**
 * Formats a date with full details including seconds.
 * Returns a format like "January 15, 2026 at 3:45:30 PM EST"
 * 
 * @param {string|Date|null} dateInput - ISO date string or Date object
 * @param {string} timezone - IANA timezone name (e.g., "America/New_York")
 * @returns {string} Formatted date/time string with timezone, or empty string if invalid
 */
export const formatDateTimeFull = (dateInput, timezone = 'UTC') => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '';
    
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone,
      timeZoneName: 'short'
    };
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
    console.warn('Full DateTime formatting error:', error);
    return '';
  }
};

/**
 * Formats time only according to the user's timezone.
 * Returns a format like "3:45 PM"
 * 
 * @param {string|Date|null} dateInput - ISO date string or Date object
 * @param {string} timezone - IANA timezone name (e.g., "America/New_York")
 * @returns {string} Formatted time string, or empty string if invalid
 */
export const formatTime = (dateInput, timezone = 'UTC') => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '';
    
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone
    };
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
    console.warn('Time formatting error:', error);
    return '';
  }
};

/**
 * Formats a date relative to now (e.g., "2 hours ago", "in 3 days")
 * Note: This is timezone-agnostic since it's relative to "now"
 * 
 * @param {string|Date|null} dateInput - ISO date string or Date object
 * @param {object} options - Options for Intl.RelativeTimeFormat
 * @returns {string} Relative time string, or empty string if invalid
 */
export const formatRelativeTime = (dateInput, options = {}) => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diffInSeconds = (date.getTime() - now.getTime()) / 1000;
    const diffInMinutes = diffInSeconds / 60;
    const diffInHours = diffInMinutes / 60;
    const diffInDays = diffInHours / 24;
    const diffInWeeks = diffInDays / 7;
    const diffInMonths = diffInDays / 30;
    const diffInYears = diffInDays / 365;
    
    const rtf = new Intl.RelativeTimeFormat('en', { 
      numeric: 'auto',
      ...options 
    });
    
    // Choose the most appropriate unit
    if (Math.abs(diffInSeconds) < 60) {
      return rtf.format(Math.round(diffInSeconds), 'second');
    } else if (Math.abs(diffInMinutes) < 60) {
      return rtf.format(Math.round(diffInMinutes), 'minute');
    } else if (Math.abs(diffInHours) < 24) {
      return rtf.format(Math.round(diffInHours), 'hour');
    } else if (Math.abs(diffInDays) < 7) {
      return rtf.format(Math.round(diffInDays), 'day');
    } else if (Math.abs(diffInWeeks) < 4) {
      return rtf.format(Math.round(diffInWeeks), 'week');
    } else if (Math.abs(diffInMonths) < 12) {
      return rtf.format(Math.round(diffInMonths), 'month');
    } else {
      return rtf.format(Math.round(diffInYears), 'year');
    }
  } catch (error) {
    console.warn('Relative time formatting error:', error);
    return '';
  }
};

/**
 * Formats a date for display in short form (useful for cards, lists)
 * Shows "Today", "Yesterday", or "Jan 15" for current year, "Jan 15, 2025" otherwise
 * 
 * @param {string|Date|null} dateInput - ISO date string or Date object
 * @param {string} timezone - IANA timezone name (e.g., "America/New_York")
 * @returns {string} Smart formatted date string
 */
export const formatDateSmart = (dateInput, timezone = 'UTC') => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    
    // Format both dates in the user's timezone for comparison
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone
    });
    
    const dateStr = dateFormatter.format(date);
    const nowStr = dateFormatter.format(now);
    
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = dateFormatter.format(yesterday);
    
    if (dateStr === nowStr) {
      return 'Today';
    } else if (dateStr === yesterdayStr) {
      return 'Yesterday';
    }
    
    // Check if same year
    const dateYear = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: timezone }).format(date);
    const nowYear = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: timezone }).format(now);
    
    const options = {
      month: 'short',
      day: 'numeric',
      timeZone: timezone
    };
    
    if (dateYear !== nowYear) {
      options.year = 'numeric';
    }
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
    console.warn('Smart date formatting error:', error);
    return '';
  }
};

/**
 * Detects the user's browser timezone
 * @returns {string} IANA timezone name (e.g., "America/New_York")
 */
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/**
 * Common timezone options for dropdowns
 * Matches the backend COMMON_TIMEZONES list
 */
export const COMMON_TIMEZONES = [
  { id: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { id: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { id: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { id: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { id: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { id: 'America/Anchorage', label: 'Alaska' },
  { id: 'Pacific/Honolulu', label: 'Hawaii' },
  { id: 'Europe/London', label: 'London' },
  { id: 'Europe/Paris', label: 'Paris, Berlin, Rome' },
  { id: 'Europe/Moscow', label: 'Moscow' },
  { id: 'Asia/Dubai', label: 'Dubai' },
  { id: 'Asia/Kolkata', label: 'Mumbai, New Delhi' },
  { id: 'Asia/Singapore', label: 'Singapore' },
  { id: 'Asia/Tokyo', label: 'Tokyo' },
  { id: 'Asia/Shanghai', label: 'Beijing, Shanghai' },
  { id: 'Australia/Sydney', label: 'Sydney' },
  { id: 'Pacific/Auckland', label: 'Auckland' },
];
